import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminDiagnosticsApi, type DiagnosticAssignmentRow, type LevelGradeItem } from '@/api/adminDiagnostics'
import { ModalShell } from './ModalShell'

export function GradeModal({
  assignment, onClose, onDone,
}: {
  assignment: DiagnosticAssignmentRow
  onClose: () => void
  onDone: () => void
}) {
  const { t } = useTranslation('admin')
  const queryClient = useQueryClient()
  const levelQ = useQuery({
    queryKey: ['diagnostic-level', assignment.id],
    queryFn: () => adminDiagnosticsApi.getLevel(assignment.id),
  })
  const items = levelQ.data?.data.data.items ?? []
  const [marks, setMarks] = useState<Record<number, boolean>>({})

  const markedCount = items.reduce((count, it) => count + (marks[it.seq] === undefined ? 0 : 1), 0)
  const correctCount = items.reduce((count, it) => count + (marks[it.seq] === true ? 1 : 0), 0)
  const wrongCount = items.reduce((count, it) => count + (marks[it.seq] === false ? 1 : 0), 0)
  const hasUnmarked = markedCount < items.length

  const markRemainingWrong = () => {
    setMarks((prev) => {
      const next = { ...prev }
      items.forEach((it) => {
        if (next[it.seq] === undefined) next[it.seq] = false
      })
      return next
    })
  }

  const grade = useMutation({
    mutationFn: () => {
      const grades: LevelGradeItem[] = items.map((it) => ({ seq: it.seq, quizId: it.quizId, isCorrect: marks[it.seq] ?? false }))
      return adminDiagnosticsApi.grade(assignment.id, { grades })
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['lc', 'admin', 'diagnosticReport', assignment.userId] }),
        queryClient.invalidateQueries({ queryKey: ['aiqDiagnosticReport', assignment.userId] }),
      ])
      onDone()
    },
  })

  return (
    <ModalShell title={t('diagnostic.gradeModal.title')} onClose={onClose}>
      {levelQ.isLoading ? (
        <div className="py-6 text-center text-gray-500">{t('diagnostic.gradeModal.loading')}</div>
      ) : (
        <>
          <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <p className="text-sm text-gray-700">{t('diagnostic.gradeModal.instruction')}</p>
            <p className="mt-1 text-sm font-semibold text-gray-900" aria-live="polite">
              {t('diagnostic.gradeModal.summary', {
                marked: markedCount,
                total: items.length,
                correct: correctCount,
                wrong: wrongCount,
              })}
            </p>
          </div>
          <div
            role="grid"
            aria-label={t('diagnostic.gradeModal.answerGridLabel')}
            className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4"
          >
            {items.map((it) => (
              <div
                key={it.seq}
                role="gridcell"
                title={it.title ?? undefined}
                className={`rounded-lg border p-2 transition-colors ${
                  marks[it.seq] === undefined ? 'border-gray-200 bg-white' : 'border-gray-300 bg-gray-50'
                }`}
              >
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-gray-700">
                    {t('diagnostic.gradeModal.questionShort', { n: it.seq + 1 })}
                  </span>
                  {marks[it.seq] === undefined && <span className="h-1.5 w-1.5 rounded-full bg-gray-300" aria-hidden="true" />}
                </div>
                <div className="grid grid-cols-2 gap-1">
                  <button
                    type="button"
                    aria-label={`${t('diagnostic.gradeModal.questionShort', { n: it.seq + 1 })} O`}
                    onClick={() => setMarks((m) => ({ ...m, [it.seq]: true }))}
                    className={`rounded-md px-2 py-1.5 text-sm font-semibold transition-colors ${
                      marks[it.seq] === true
                        ? 'bg-green-600 text-white shadow-sm'
                        : 'bg-green-50 text-green-700 hover:bg-green-100'
                    }`}
                  >
                    O
                  </button>
                  <button
                    type="button"
                    aria-label={`${t('diagnostic.gradeModal.questionShort', { n: it.seq + 1 })} X`}
                    onClick={() => setMarks((m) => ({ ...m, [it.seq]: false }))}
                    className={`rounded-md px-2 py-1.5 text-sm font-semibold transition-colors ${
                      marks[it.seq] === false
                        ? 'bg-red-600 text-white shadow-sm'
                        : 'bg-red-50 text-red-700 hover:bg-red-100'
                    }`}
                  >
                    X
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={markRemainingWrong}
              disabled={!hasUnmarked || items.length === 0}
              className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 disabled:opacity-50"
            >
              {t('diagnostic.gradeModal.markRemainingWrong')}
            </button>
            <button
              type="button"
              onClick={() => setMarks({})}
              disabled={markedCount === 0}
              className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 disabled:opacity-50"
            >
              {t('diagnostic.gradeModal.reset')}
            </button>
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-300 text-sm">{t('diagnostic.gradeModal.close')}</button>
            <button type="button" disabled={grade.isPending || items.length === 0 || hasUnmarked} onClick={() => grade.mutate()} className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm disabled:opacity-50">{t('diagnostic.gradeModal.submit')}</button>
          </div>
        </>
      )}
    </ModalShell>
  )
}
